import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Table, Card, Spinner, Alert, Button, Badge, Form, InputGroup, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCreditCard,
  faSearch,
  faTimes,
  faEye,
  faExternalLinkAlt,
  faSort,
  faSortDown,
  faSortUp,
} from '@fortawesome/free-solid-svg-icons';
import { getDeviceShareUrl } from '../../utils/shareLinks';
import Pagination from '../common/Pagination';
import PaymentDetailModal from './PaymentDetailModal';
import {
  getPayments,
  getPaymentById,
  getPaymentProductList,
} from '../../services/paymentService';
import { formatUsdLabel } from '../../utils/chartLabels';
import useDebounce from '../../hooks/useDebounce';

const PER_PAGE = 10;
const SEARCH_DEBOUNCE_MS = 400;
const STORAGE_PREFIX = 'payments_filter_';

const FILTER_STORAGE_KEYS = [
  'deviceIdFilter',
  'productIdFilter',
  'eventTypeFilter',
  'countryCodeFilter',
  'sortBy',
  'sortOrder',
  'currentPage',
];

const getSavedFilter = (key, defaultValue) => {
  try {
    const saved = localStorage.getItem(`${STORAGE_PREFIX}${key}`);
    return saved !== null ? JSON.parse(saved) : defaultValue;
  } catch {
    return defaultValue;
  }
};

const renderSortIcon = (sortBy, sortOrder, field) => {
  if (sortBy !== field) {
    return <FontAwesomeIcon icon={faSort} className="ms-1 text-muted" />;
  }
  return sortOrder === 'asc'
    ? <FontAwesomeIcon icon={faSortUp} className="ms-1" />
    : <FontAwesomeIcon icon={faSortDown} className="ms-1" />;
};

const COUNTRY_CODES = [
  'AE','AI','AM','AO','AR','AT','AU','AZ','BB','BE','BF','BG','BH','BM','BO','BR','BS','BW','BY',
  'CA','CH','CI','CL','CN','CO','CR','CY','CZ','DE','DK','DO','EC','EE','EG','ES','FI','FJ','FR',
  'GB','GE','GH','GR','GY','HK','HN','HR','HU','ID','IE','IL','IN','IS','IT','JM','JO','JP','KE',
  'KG','KH','KR','KW','KZ','LB','LK','LT','LU','LV','MD','MK','MN','MT','MU','MX','MY','NA','NG',
  'NL','NO','NZ','OM','PA','PE','PH','PK','PL','PT','PY','QA','RO','RU','SA','SC','SE','SG','SI',
  'SK','TH','TR','TT','TW','TZ','UA','UG','US','UY','VN','ZA','ZW',
];

const formatEventTypeLabel = (type) => {
  if (type === 'INITIAL_PURCHASE') return 'Subscription';
  if (type === 'NON_RENEWING_PURCHASE') return 'One-time';
  return type?.replace(/_/g, ' ') || '—';
};

const deviceDetailPath = (deviceId) =>
  `/devices/detail?device_id=${encodeURIComponent(deviceId)}`;

const PaymentMobileCard = ({ row, onOpenDetail, formatUsd }) => (
  <div
    className="payments-mobile-card"
    role="button"
    tabIndex={0}
    onClick={() => onOpenDetail(row)}
    onKeyDown={(e) => e.key === 'Enter' && onOpenDetail(row)}
  >
    <div className="payments-mobile-card-body">
      <div className="payments-mobile-top">
        <Badge bg="secondary" className="text-white">
          {formatEventTypeLabel(row.event_type)}
        </Badge>
        <span className="payments-mobile-usd">{formatUsd(row)}</span>
      </div>
      <div className="payments-mobile-date">{row.purchased_at || '—'}</div>

      <div className="payments-mobile-row">
        <span className="payments-field-label">Device</span>
        <Link
          to={deviceDetailPath(row.device_id)}
          className="payments-device-link payments-field-value payments-field-value--mono"
          onClick={(e) => e.stopPropagation()}
        >
          {row.device_id}
        </Link>
      </div>

      <div className="payments-mobile-row">
        <span className="payments-field-label">Product</span>
        <span className="payments-field-value payments-field-value--mono">{row.product_id || '—'}</span>
      </div>

      <div className="payments-mobile-footer">
        <Button
          size="sm"
          variant="outline-success"
          href={getDeviceShareUrl(row.device_id)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />
          Share
        </Button>
        <Button
          size="sm"
          variant="outline-primary"
          onClick={(e) => {
            e.stopPropagation();
            onOpenDetail(row);
          }}
        >
          <FontAwesomeIcon icon={faEye} className="me-1" />
          Detail
        </Button>
      </div>
    </div>
  </div>
);

const Payments = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlDeviceId = searchParams.get('device_id') || '';
  const skipPageResetRef = useRef(true);

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(() => getSavedFilter('currentPage', 1));
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, per_page: PER_PAGE, total: 0 });

  const [deviceIdFilter, setDeviceIdFilter] = useState(
    () => urlDeviceId || getSavedFilter('deviceIdFilter', ''),
  );
  const [productIdFilter, setProductIdFilter] = useState(() => getSavedFilter('productIdFilter', ''));
  const [eventTypeFilter, setEventTypeFilter] = useState(() => getSavedFilter('eventTypeFilter', ''));
  const [countryCodeFilter, setCountryCodeFilter] = useState(() => getSavedFilter('countryCodeFilter', ''));
  const [sortBy, setSortBy] = useState(() => getSavedFilter('sortBy', ''));
  const [sortOrder, setSortOrder] = useState(() => getSavedFilter('sortOrder', 'desc'));

  const [productList, setProductList] = useState([]);

  const debouncedDeviceId = useDebounce(deviceIdFilter, SEARCH_DEBOUNCE_MS);

  const [detailShow, setDetailShow] = useState(false);
  const [detailPayment, setDetailPayment] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (urlDeviceId) {
      setDeviceIdFilter(urlDeviceId);
    }
  }, [urlDeviceId]);

  useEffect(() => {
    try {
      localStorage.setItem(`${STORAGE_PREFIX}deviceIdFilter`, JSON.stringify(deviceIdFilter));
      localStorage.setItem(`${STORAGE_PREFIX}productIdFilter`, JSON.stringify(productIdFilter));
      localStorage.setItem(`${STORAGE_PREFIX}eventTypeFilter`, JSON.stringify(eventTypeFilter));
      localStorage.setItem(`${STORAGE_PREFIX}countryCodeFilter`, JSON.stringify(countryCodeFilter));
      localStorage.setItem(`${STORAGE_PREFIX}sortBy`, JSON.stringify(sortBy));
      localStorage.setItem(`${STORAGE_PREFIX}sortOrder`, JSON.stringify(sortOrder));
      localStorage.setItem(`${STORAGE_PREFIX}currentPage`, JSON.stringify(currentPage));
    } catch {
      // ignore storage errors
    }
  }, [deviceIdFilter, productIdFilter, eventTypeFilter, countryCodeFilter, sortBy, sortOrder, currentPage]);

  useEffect(() => {
    getPaymentProductList()
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data?.data)) {
          setProductList(res.data.data);
        }
      })
      .catch(() => {});
  }, []);

  const fetchPayments = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { per_page: PER_PAGE, page: currentPage, environment: 'PRODUCTION' };
      if (debouncedDeviceId.trim()) params.device_id = debouncedDeviceId.trim();
      if (productIdFilter) params.product_id = productIdFilter;
      if (eventTypeFilter) params.event_type = eventTypeFilter;
      if (countryCodeFilter) params.country_code = countryCodeFilter;
      if (sortBy) { params.sort_by = sortBy; params.sort_order = sortOrder; }
      const res = await getPayments(params);
      const data = res.data;
      if (data?.success && Array.isArray(data?.data)) {
        setPayments(data.data);
        if (data.extra) {
          setPagination({
            current_page: data.extra.current_page ?? currentPage,
            last_page: data.extra.last_page ?? 1,
            per_page: data.extra.per_page ?? PER_PAGE,
            total: data.extra.total ?? 0,
          });
        }
      } else {
        setPayments([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch payments');
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (skipPageResetRef.current) {
      skipPageResetRef.current = false;
      return;
    }
    setCurrentPage(1);
  }, [debouncedDeviceId, productIdFilter, eventTypeFilter, countryCodeFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchPayments();
  }, [currentPage, debouncedDeviceId, productIdFilter, eventTypeFilter, countryCodeFilter, sortBy, sortOrder]);

  const handlePageChange = (page) => setCurrentPage(page);

  const handleSortAmount = () => {
    if (sortBy === 'amount') {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy('amount');
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setDeviceIdFilter('');
    setProductIdFilter('');
    setEventTypeFilter('');
    setCountryCodeFilter('');
    setSortBy('');
    setSortOrder('desc');
    setCurrentPage(1);
    FILTER_STORAGE_KEYS.forEach((key) => {
      try {
        localStorage.removeItem(`${STORAGE_PREFIX}${key}`);
      } catch {
        // ignore
      }
    });
    if (urlDeviceId) {
      navigate('/payments', { replace: true });
    }
  };

  const openDetail = async (row) => {
    setDetailShow(true);
    setDetailPayment(row);
    setDetailLoading(true);
    try {
      const res = await getPaymentById(row.id);
      if (res.data?.success) setDetailPayment(res.data.data);
    } catch {
      // keep row data
    } finally {
      setDetailLoading(false);
    }
  };

  const formatUsd = (row) => {
    const val = row.price_usd ?? row.display_amount;
    return val != null ? formatUsdLabel(val) : '—';
  };

  const paginationBlock = (position) =>
    pagination.total > 0 ? (
      <Pagination
        currentPage={pagination.current_page}
        totalItems={pagination.total}
        itemsPerPage={pagination.per_page}
        onPageChange={handlePageChange}
        position={position}
      />
    ) : null;

  return (
    <div className="payments-page dashboard-list-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mb-md-4">
        <h2 className="payments-page-title mb-0">
          <FontAwesomeIcon icon={faCreditCard} className="me-2" />
          Payments
        </h2>
      </div>
      <p className="text-muted mb-3 payments-page-desc">
        Revenue from App Store (Production). All amounts shown in USD (converted from storefront currency when needed).
      </p>

      {error && (
        <Alert variant="danger" className="mb-3" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Body className="p-3 p-md-4">
          <Row className="g-2 align-items-end">
            <Col xs={12}>
              <Form.Label className="small mb-1">Device ID</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  value={deviceIdFilter}
                  onChange={(e) => setDeviceIdFilter(e.target.value)}
                  placeholder="Search by device ID..."
                />
                {deviceIdFilter && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setDeviceIdFilter('')}
                    aria-label="Clear device search"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                )}
              </InputGroup>
            </Col>
            <Col xs={12} md={6}>
              <Form.Label className="small mb-1">Product</Form.Label>
              <Form.Select
                value={productIdFilter}
                onChange={(e) => setProductIdFilter(e.target.value)}
              >
                <option value="">All products</option>
                {productList.map((p) => (
                  <option key={p.product_id} value={p.product_id}>
                    {p.name ? `${p.name} — ${p.product_id}` : p.product_id}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} md={6}>
              <Form.Label className="small mb-1">Event type</Form.Label>
              <Form.Select
                value={eventTypeFilter}
                onChange={(e) => setEventTypeFilter(e.target.value)}
              >
                <option value="">All</option>
                <option value="INITIAL_PURCHASE">INITIAL_PURCHASE</option>
                <option value="NON_RENEWING_PURCHASE">NON_RENEWING_PURCHASE</option>
              </Form.Select>
            </Col>
            <Col xs={12} md={6}>
              <Form.Label className="small mb-1">Country</Form.Label>
              <Form.Select
                value={countryCodeFilter}
                onChange={(e) => setCountryCodeFilter(e.target.value)}
              >
                <option value="">All countries</option>
                {COUNTRY_CODES.map((code) => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} className="d-md-none">
              <Button
                variant="outline-secondary"
                size="sm"
                className="w-100"
                onClick={handleSortAmount}
              >
                Sort by amount {renderSortIcon(sortBy, sortOrder, 'amount')}
              </Button>
            </Col>
            <Col xs={12} className="d-flex justify-content-end">
              <Button variant="outline-secondary" onClick={handleResetFilters}>
                Reset filters
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body className="p-2 p-md-3">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center text-muted py-4">No payments found</div>
          ) : (
            <>
              <div className="payments-pagination-top">{paginationBlock('top')}</div>

              {/* Desktop: table */}
              <div className="payments-list-desktop table-responsive">
                <Table hover className="mb-0">
                  <thead>
                    <tr>
                      <th>Purchased</th>
                      <th>Type</th>
                      <th>Device</th>
                      <th>Product</th>
                      <th
                        className="payments-sortable-th"
                        onClick={handleSortAmount}
                        aria-sort={
                          sortBy === 'amount'
                            ? sortOrder === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : 'none'
                        }
                      >
                        Amount (USD) {renderSortIcon(sortBy, sortOrder, 'amount')}
                      </th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((row) => (
                      <tr key={row.id} style={{ cursor: 'pointer' }} onClick={() => openDetail(row)}>
                        <td className="small">{row.purchased_at || '—'}</td>
                        <td>
                          <Badge bg="secondary" className="text-white" style={{ fontSize: '0.7rem' }}>
                            {row.event_type?.replace(/_/g, ' ') || '—'}
                          </Badge>
                        </td>
                        <td className="small" onClick={(e) => e.stopPropagation()}>
                          <Link
                            to={deviceDetailPath(row.device_id)}
                            className="payments-device-link font-monospace"
                          >
                            {row.device_id?.slice(0, 8)}…
                          </Link>
                        </td>
                        <td className="small">{row.product_id}</td>
                        <td>{formatUsd(row)}</td>
                        <td className="text-nowrap">
                          <Button
                            size="sm"
                            variant="outline-success"
                            className="me-1"
                            href={getDeviceShareUrl(row.device_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title="Open device share link"
                          >
                            <FontAwesomeIcon icon={faExternalLinkAlt} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDetail(row);
                            }}
                            title="Payment detail"
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Mobile: stacked cards */}
              <div className="payments-mobile-list">
                {payments.map((row) => (
                  <PaymentMobileCard
                    key={row.id}
                    row={row}
                    onOpenDetail={openDetail}
                    formatUsd={formatUsd}
                  />
                ))}
              </div>

              {paginationBlock('bottom')}
            </>
          )}
        </Card.Body>
      </Card>

      <PaymentDetailModal
        show={detailShow}
        onHide={() => setDetailShow(false)}
        payment={detailPayment}
        loading={detailLoading}
      />
    </div>
  );
};

export default Payments;
